import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { WorkflowExecution } from '@common/entities/workflows/workflow-execution.entity';
import { WorkflowType } from '@common/entities/workflows/workflow-type.type';

import {
    HttpService,
    IWorkflowTask,
    RecordsService,
    TaskAction,
    TaskId,
    WaitForRecordStatusUpdate,
    WorkflowTask
} from '@mediahaven/shared';
import {
    ApiFragment,
    ApiQuery,
    ApiQueryBuilder,
    CustomHttpParamEncoder,
    MediaObjectId,
    Paginated,
    PaginatedResults,
    PaginationInfo,
    SortInfo,
    mapToPaginatedResult
} from '@stylehaven/components';

@Injectable({ providedIn: 'root' })
export class WorkflowService {
    constructor(
        private readonly http: HttpService,
        private readonly recordsService: RecordsService
    ) {}

    private readonly WORKFLOW_EXCECUTION_ENDPOINT: string = `workflow/api/executions`;
    private readonly TASKS_ENDPOINT: string = `workflow/api/tasks`;

    public getUserTasks(
        query: ApiQuery,
        paginationInfo: PaginationInfo,
        sortInfo: SortInfo
    ): Observable<PaginatedResults<WorkflowTask>> {
        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() })
            .set('q', ApiQueryBuilder.queryToLuceneQuery(query))
            .set('startIndex', (paginationInfo.page * paginationInfo.resultsPerPage).toString())
            .set('nrOfResults', paginationInfo.resultsPerPage.toString())
            .set('sort', sortInfo.field)
            .set('direction', sortInfo.direction)
            .set('onlyCompletable', 'true');

        const request = `${this.TASKS_ENDPOINT}/active?${params.toString()}`;

        return this.http
            .get<Paginated<IWorkflowTask>>(request)
            .pipe(
                map(results => ({
                    ...results,
                    Results: results.Results.map(task => new WorkflowTask(task))
                }))
            )
            .pipe(map(results => mapToPaginatedResult(results, paginationInfo.resultsPerPage)));
    }

    public getTask(taskId: TaskId): Observable<WorkflowTask> {
        const request = `${this.TASKS_ENDPOINT}/${taskId}/active`;

        return this.http.get<IWorkflowTask>(request).pipe(map(result => new WorkflowTask(result)));
    }

    public submitRecordForEvaluation(fragment: ApiFragment): Observable<ApiFragment> {
        return this.startWorkflowExecution(fragment.getMediaObjectId(), WorkflowType.EVALUATION).pipe(
            switchMap(() =>
                WaitForRecordStatusUpdate(fragment.getFragmentId(), this.recordsService, fragment.getRecordStatus())
            )
        );
    }

    public submitTaskAction(taskId: TaskId, taskAction: TaskAction): Observable<void> {
        return this.http.post<void>(`${this.TASKS_ENDPOINT}/${taskId}`, taskAction);
    }

    public publishRecord(fragment: ApiFragment): Observable<ApiFragment> {
        return this.startWorkflowExecution(fragment.getMediaObjectId(), WorkflowType.PUBLISH).pipe(
            switchMap(() =>
                WaitForRecordStatusUpdate(fragment.getFragmentId(), this.recordsService, fragment.getRecordStatus())
            )
        );
    }

    private startWorkflowExecution(umid: MediaObjectId, workflow: WorkflowType): Observable<boolean> {
        const workflowExecution: WorkflowExecution = {
            ProcessName: workflow,
            Umid: umid
        };

        return this.http.post<boolean>(this.WORKFLOW_EXCECUTION_ENDPOINT, workflowExecution);
    }
}
